import { Box, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useState } from 'react';
import type { Project } from '../projects';
import { ImagePreviewModal } from './ImagePreviewModal';
import { LazyImage } from '@/components/LazyImage';
import { SkillBadge } from '@/components/SkillBadge';

const CardRoot = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '160px 1fr',
  gap: theme.spacing(3),
  padding: theme.spacing(2.5),
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  position: 'relative',
  height: '10rem',
  alignContent: 'center',
}));

const ProjectImageButton = styled('button')(({ theme }) => ({
  all: 'unset',
  cursor: 'zoom-in',
  display: 'block',
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: '0 0 8px rgba(125,211,199,0.15)',
  height: '100%',
  overflow: 'hidden',

  '&:hover': {
    boxShadow: '0 0 12px rgba(125,211,199,0.35)',
  },
}));

export function ProjectCard({ project }: { project: Project }) {
  const [previewOpen, setPreviewOpen] = useState(false);

  return (
    <>
      <CardRoot>
        <ProjectImageButton onClick={() => setPreviewOpen(true)}>
          <LazyImage
            src={project.imageUrl}
            alt={project.title}
            style={{
              display: 'block',
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        </ProjectImageButton>

        <Stack spacing={1.5}>
          <Typography
            variant="h5"
            component="a"
            href={project.link}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: 'primary.main',
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {project.title}
          </Typography>

          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {project.description}
          </Typography>

          <Stack direction="row" spacing={1} flexWrap="wrap">
            {project.technologies.map((tech) => (
              <SkillBadge
                key={tech}
                label={tech}
                sx={{
                  fontSize: '0.65rem',
                  height: 22,
                }}
              />
            ))}
          </Stack>
        </Stack>
      </CardRoot>
      <ImagePreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        title={project.title}
        imageUrl={project.imageUrl}
      />
    </>
  );
}
